import {ApplicationRef, Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {EventBus} from "./event/EventBus";
import {SessionService} from "./services/configuration/session.service";
import {BugsnagLoggingService} from "./services/logging/bugsnag-logging.service";
import {TimerManagerService} from "./services/configuration/timer.manager.service";
import {StudentConfiguration} from "./services/assignment/student.configuration";
import {KeyboardService} from "./services/input/keyboard-service";
import {TranslateService} from "@ngx-translate/core";
import {ActivatedRoute} from "@angular/router";
import {MessageService} from "./services/notification/message.service";
import {GAEventService} from "./services/analytics/GAEventServce";
import {ComponentBase} from "./base/ComponentBase";
import {filter} from "rxjs/operators";
import {EXIT_STATUS, ExitEvent, ReEnterEvent} from "./event/ExitEvent";
import {NavigationEvent} from "./event/NavigationEvent";
import {PleaseWaitEvent, PleaseWaitTarget} from "./event/ContentEvents";
import {BrowserBoot} from "./startup/browser.boot";
import {NavService} from "./services/nav/nav.service";

@Component({
  selector: 'reading-assistant',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent extends ComponentBase implements OnInit, OnDestroy{
  title = 'rav2-client-app-ng';

  public showRA = false;

  constructor(private browserBoot: BrowserBoot,
              private eventBus: EventBus,
              private navService: NavService,
              private session: SessionService,
              private log: BugsnagLoggingService,
              private timerManagerService: TimerManagerService,
              private studentConfiguration: StudentConfiguration,
              private keyboardService: KeyboardService,
              private translateService: TranslateService,
              private applicationRef: ApplicationRef,
              private activatedRoute: ActivatedRoute,
              private messageService: MessageService,
              private gaEventService: GAEventService
  ) {
    super("AppComponent");
  }

  ngOnInit(): void {
    this.log.log("ngOnInit AppComponent");
    this.gaEventService.startDurationEvent("app", "library-load", "start");
    this.translateService.use("en");
    this.translateService.setDefaultLang("en");


    this.showRA = true;
    window.onbeforeunload = (e) => this.onBeforeUnload(e);

    let sub = this.browserBoot.initialize().subscribe(() => {
          this.messageService.loadMessages().subscribe(() => {
              this.session.init().subscribe(() => {
                  this.timerManagerService.initialize();
                  this.activatedRoute.url.subscribe((url) => {
                      if (!window.location.hash.match("reading")) {
                          this.goToLibrary();
                      }
                  });
              });
          });
    });
    this.subscriptions.push(sub);

    sub = this.eventBus.pipe(filter(event => event instanceof ExitEvent)).subscribe(() => this.onExit());
    this.subscriptions.push(sub);

    sub = this.eventBus.pipe(filter(event => event instanceof ReEnterEvent)).subscribe(() => this.onReEnter());
    this.subscriptions.push(sub);
  }

  onReEnter(): void {
    this.log.log("onReEnter");

    this.showRA = true;
    this.applicationRef.tick(); // "showRA" change seems to have trouble taking effect when coming back into app.

    window.onbeforeunload = (e) => this.onBeforeUnload(e);

    this.session.init().subscribe(() => this.goToLibrary());
  }

  onExit(): void {
    this.log.log("onExit");
    this.eventBus.next(new NavigationEvent(["hide"]));
    this.showRA = false;
    this.timerManagerService.stopAllTimers();
    document.dispatchEvent(new Event("productExit"));
    window.onbeforeunload = undefined;
  }

  ngOnDestroy(): void {
    this.log.log("ngOnDestroy");
    window.onbeforeunload = undefined;
  }

  @HostListener("window:keydown", ["$event"])
  onKey(event: KeyboardEvent): void {
    this.keyboardService.onKey(event);
  }

  private onBeforeUnload(e): void {
      let sub = this.session.exit(true).subscribe(
          () => {
              document.dispatchEvent(new Event("productExit"));
              window.onbeforeunload = undefined;
          },
          () => {
              this.log.error("session exit error on user window close");
              document.dispatchEvent(new Event("productExit"));
              window.onbeforeunload = undefined;
          }
      );
      this.subscriptions.push(sub);
  }

  private goToLibrary(): void {
    this.log.log("calling go to library");
    this.eventBus.next(new PleaseWaitEvent({show: true, audio: true, destination: PleaseWaitTarget.LIBRARY}));
    this.eventBus.next(new NavigationEvent(["library"]));
  }


}
